import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import axios, { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { backofficeApi } from '@/lib/backoffice-api'
import { setStaffAccessToken } from '@/lib/backoffice-auth'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

interface AcceptForm {
  name: string
  password: string
}

interface InviteInfo {
  email: string
  role: 'OWNER' | 'STAFF'
  expiresAt: string
}

export function AcceptStaffInvitePage() {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptForm>()

  useEffect(() => {
    if (!token) return
    axios
      .get<InviteInfo>(`${BASE_URL}/backoffice/auth/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => {
        const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
        setLoadError(msg ?? 'Convite inválido ou expirado.')
      })
  }, [token])

  async function onSubmit(data: AcceptForm) {
    setApiError(null)
    try {
      const res = await backofficeApi.post<{ accessToken: string }>(
        `/backoffice/auth/accept-invite/${token}`,
        data,
      )
      setStaffAccessToken(res.data.accessToken)
      navigate('/backoffice', { replace: true })
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
      setApiError(msg ?? 'Erro ao aceitar convite.')
    }
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-sm space-y-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">Convite indisponível</h1>
          <p className="text-sm text-muted-foreground">{loadError}</p>
        </div>
      </div>
    )
  }

  if (!invite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Aceitar convite
          </h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">{invite.email}</span> — {invite.role === 'OWNER' ? 'Owner' : 'Operador'} do backoffice
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              autoComplete="name"
              {...register('name', { required: 'Informe seu nome', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              {...register('password', { required: 'Informe uma senha', minLength: { value: 8, message: 'Mínimo 8 caracteres' } })}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>

          {apiError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
            {isSubmitting ? 'Criando…' : 'Criar conta e entrar'}
          </Button>
        </form>
      </div>
    </div>
  )
}
